import API from "./api";

class SymbolsCacheService {
  constructor() {
    this.cache = new Map();
  }

  async getSymbolsForAccountSet(accountSet) {
    if (!accountSet || (!accountSet.id && !accountSet._id)) {
      return {
        broker1Symbols: [],
        broker2Symbols: [],
        success: false,
        error: "Invalid account set",
      };
    }

    const accountSetId = accountSet.id || accountSet._id;
    const cacheKey = `accountSet_${accountSetId}`;

    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < 300000) {
        return cached.data;
      }
    }

    try {
      const response = await API.get(`/symbols/account-set/${accountSetId}`);

      if (response.data && response.data.success) {
        const result = {
          broker1Symbols: response.data.broker1Symbols || [],
          broker2Symbols: response.data.broker2Symbols || [],
          success: true,
          source: "account_set_cache",
          meta: response.data.meta,
        };

        this.cache.set(cacheKey, { data: result, timestamp: Date.now() });
        return result;
      }

      return await this.getSymbolsForAccountSetFallback(accountSet);
    } catch (error) {
      console.error("❌ SymbolsCache: Failed to load symbols for account set:", error);
      try {
        return await this.getSymbolsForAccountSetFallback(accountSet);
      } catch (fallbackError) {
        return {
          broker1Symbols: [],
          broker2Symbols: [],
          success: false,
          error: fallbackError.message,
        };
      }
    }
  }

  async getSymbolsForAccountSetFallback(accountSet) {
    if (!accountSet.brokers || accountSet.brokers.length < 2) {
      return {
        broker1Symbols: [],
        broker2Symbols: [],
        success: false,
        error: "Insufficient brokers",
      };
    }

    const sortedBrokers = [...accountSet.brokers].sort(
      (a, b) => (a.position || 0) - (b.position || 0),
    );
    const broker1 = sortedBrokers.find((b) => b.position === 1) || sortedBrokers[0];
    const broker2 = sortedBrokers.find((b) => b.position === 2) || sortedBrokers[1];

    if (!broker1 || !broker2) {
      return {
        broker1Symbols: [],
        broker2Symbols: [],
        success: false,
        error: "Missing broker positions",
      };
    }

    const [broker1Symbols, broker2Symbols] = await Promise.all([
      this.getSymbolsForBroker(broker1),
      this.getSymbolsForBroker(broker2),
    ]);

    return {
      broker1Symbols: broker1Symbols || [],
      broker2Symbols: broker2Symbols || [],
      success: true,
      source: "fallback_cache",
    };
  }

  async getSymbolsForBroker(broker) {
    if (!broker) return [];

    const cacheKey = `${broker.brokerName}_${broker.terminal}_${broker.server}`;

    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < 300000) {
        return cached.symbols;
      }
    }

    try {
      const normalizedBroker = this.normalizeBrokerName(broker.brokerName, broker.server);
      const cacheResponse = await API.get("/symbols/cache", {
        params: {
          broker1: normalizedBroker,
          terminal: broker.terminal,
        },
      });

      if (
        cacheResponse.data &&
        cacheResponse.data.broker1 &&
        cacheResponse.data.broker1.length > 0
      ) {
        const symbols = cacheResponse.data.broker1.map((s) => s.symbol || s.display || s);
        this.cache.set(cacheKey, { symbols, timestamp: Date.now() });
        return symbols;
      }

      const fallbackResponse = await API.get("/trading/symbols", {
        params: {
          terminal: broker.terminal,
          id: broker.id || broker._id,
        },
      });

      if (fallbackResponse.data && fallbackResponse.data.success && fallbackResponse.data.symbols) {
        const raw = fallbackResponse.data.symbols;
        const symbols = Array.isArray(raw) ? raw : Object.values(raw || {});
        const processed = symbols
          .map((o) => (typeof o === "string" ? o : o.currency || o.symbol || o.name))
          .filter((sym) => sym && sym.trim())
          .map((s) => s.trim());

        this.cache.set(cacheKey, { symbols: processed, timestamp: Date.now() });
        return processed;
      }

      return [];
    } catch (error) {
      console.error(
        `❌ Failed to load symbols for ${broker.brokerName} ${broker.terminal}:`,
        error,
      );
      return [];
    }
  }

  normalizeBrokerName(brokerName, server) {
    if (brokerName) {
      return brokerName.toLowerCase().replace(/[^a-z0-9]/g, "");
    }

    if (server) {
      const parts = server.split(".");
      if (parts.length > 0) {
        let company = parts[0];
        const commonPatterns = [
          /demo$/i,
          /real$/i,
          /live$/i,
          /server$/i,
          /mt[45]$/i,
          /-\d+$/i,
          /\d+$/,
        ];

        for (const pattern of commonPatterns) {
          company = company.replace(pattern, "");
        }

        return company.replace(/[^a-z0-9]/gi, "").toLowerCase();
      }
    }

    return "unknown";
  }

  clearCache() {
    this.cache.clear();
  }

  async searchAndUpdateSymbol(searchSymbol, currentSymbols, broker) {
    if (!searchSymbol || !broker) {
      return { found: false, symbols: currentSymbols };
    }

    const symbolExists = currentSymbols.some(
      (sym) =>
        sym.toLowerCase().includes(searchSymbol.toLowerCase()) ||
        searchSymbol.toLowerCase().includes(sym.toLowerCase()),
    );

    if (symbolExists) {
      return { found: true, symbols: currentSymbols };
    }

    try {
      const refreshedSymbols = await this.refreshSymbolsForBroker(broker);
      const foundInRefresh = refreshedSymbols.some(
        (sym) =>
          sym.toLowerCase().includes(searchSymbol.toLowerCase()) ||
          searchSymbol.toLowerCase().includes(sym.toLowerCase()),
      );

      if (foundInRefresh) {
        return { found: true, symbols: refreshedSymbols };
      }
      return { found: false, symbols: refreshedSymbols };
    } catch (error) {
      console.error(`❌ Failed to search and update symbol "${searchSymbol}":`, error);
      return { found: false, symbols: currentSymbols };
    }
  }

  async refreshSymbolsForBroker(broker) {
    if (!broker) return [];

    try {
      if (broker.id || broker._id) {
        await API.post("/trading/refresh-symbols", {
          brokerId: broker.id || broker._id,
          terminal: broker.terminal,
        });
      }

      const cacheKey = `${broker.brokerName}_${broker.terminal}_${broker.server}`;
      this.cache.delete(cacheKey);

      const accountSetKeys = Array.from(this.cache.keys()).filter((key) =>
        key.startsWith("accountSet_"),
      );
      accountSetKeys.forEach((key) => this.cache.delete(key));

      return await this.getSymbolsForBroker(broker);
    } catch (error) {
      console.error(`❌ Failed to refresh symbols for ${broker.brokerName}:`, error);
      return [];
    }
  }
}

const symbolsCacheService = new SymbolsCacheService();

export default symbolsCacheService;
